import { useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Scroll choreography for the landing page. Every section reuses the logo move:
// frames rotate between upright squares and diamonds that meet at the point.
// `signature` changes when Firestore data adds or removes sections, so the
// triggers are rebuilt against the new layout.
export default function useLandingMotion(rootRef, signature) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = gsap.context(() => {
      const q = (sel) => root.querySelectorAll(sel);

      // Hero: the diamonds turn back into two upright photographs and drift apart.
      if (q('.hero').length) {
        gsap.timeline({ scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom 20%', scrub: 0.8 } })
          .to('.hspin', { rotation: -45, ease: 'none' }, 0)
          .to('.hc2', { rotation: 45, ease: 'none' }, 0)
          .to('.hpos.left .hspin', { x: '-6vw', ease: 'none' }, 0)
          .to('.hpos.right .hspin', { x: '6vw', ease: 'none' }, 0)
          .to('.hdot', { autoAlpha: 0, ease: 'none' }, 0)
          .to('.hero h1', { yPercent: -18, ease: 'none' }, 0);
      }

      // Marquee leans with the scroll speed.
      const band = root.querySelector('.marquee');
      if (band) {
        ScrollTrigger.create({
          onUpdate: (s) => {
            const v = Math.min(Math.abs(s.getVelocity()) / 600, 4);
            gsap.to(band, { skewX: s.direction * -v * 1.5, duration: 0.4, overwrite: true, onComplete: () => gsap.to(band, { skewX: 0, duration: 0.8 }) });
          },
        });
      }

      // Section headings rise in.
      q('.sec-head h2, .about h2, .faq h2').forEach((el) => {
        gsap.from(el, { y: 50, duration: 1.1, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%' } });
      });

      // Service icons spin into place.
      q('.svc').forEach((el) => {
        gsap.from(el.querySelector('.ico'), { rotation: -180, scale: 0.4, duration: 1.1, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%' } });
      });

      // Works and gallery: each frame opens from a diamond into the full photo.
      q('.work .clip, .mosaic .clip, .grid3 .clip').forEach((el) => {
        gsap.fromTo(el,
          { clipPath: 'polygon(50% 8%, 92% 50%, 50% 92%, 8% 50%)' },
          { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', ease: 'none', scrollTrigger: { trigger: el, start: 'top 95%', end: 'top 50%', scrub: 0.6 } });
        const media = el.querySelector('img, video');
        if (media) gsap.fromTo(media, { scale: 1.3 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: el, start: 'top 95%', end: 'bottom top', scrub: 0.6 } });
      });

      // About numbers lift in.
      if (q('.stats').length) {
        gsap.from(q('.stat'), { y: 40, duration: 1, ease: 'expo.out', stagger: 0.1, scrollTrigger: { trigger: '.stats', start: 'top 85%' } });
      }

      // Motto: the mark makes a half turn with the scroll.
      if (q('#bandMark').length) {
        gsap.to('#bandMark .ml', { rotation: 180, transformOrigin: '50% 50%', ease: 'none', scrollTrigger: { trigger: '.band', start: 'top bottom', end: 'bottom top', scrub: 1 } });
        gsap.to('#bandMark .mr', { rotation: -180, transformOrigin: '50% 50%', ease: 'none', scrollTrigger: { trigger: '.band', start: 'top bottom', end: 'bottom top', scrub: 1 } });
        gsap.from('.band p', { y: 60, duration: 1.2, ease: 'expo.out', scrollTrigger: { trigger: '.band', start: 'top 75%' } });
      }

      // Process: the rail fills and each diamond node turns gold as you pass it.
      if (q('.steps').length) {
        gsap.to('.steps .rail i', { scaleY: 1, ease: 'none', scrollTrigger: { trigger: '.steps', start: 'top 60%', end: 'bottom 60%', scrub: 0.5 } });
        q('.step').forEach((el) => {
          ScrollTrigger.create({ trigger: el, start: 'top 60%', onEnter: () => el.classList.add('on'), onLeaveBack: () => el.classList.remove('on') });
        });
      }

      // QR phone floats in, steps slide in.
      if (q('.phone').length) {
        gsap.from('.phone', { y: 80, rotation: 4, duration: 1.4, ease: 'expo.out', scrollTrigger: { trigger: '.guests', start: 'top 70%' } });
        gsap.from(q('.qr-list > div'), { x: -30, duration: 0.9, ease: 'power3.out', stagger: 0.12, scrollTrigger: { trigger: '.qr-list', start: 'top 85%' } });
      }

      // Cards lift in.
      ['.packs', '.systems', '.films', '.reviews'].forEach((sel) => {
        const box = root.querySelector(sel);
        if (box) gsap.from(box.children, { y: 60, duration: 1, ease: 'expo.out', stagger: 0.1, scrollTrigger: { trigger: box, start: 'top 85%' } });
      });

      // Final CTA: two upright frames fly in from the sides and lock into the mark.
      if (q('.final .mk').length) {
        gsap.timeline({ scrollTrigger: { trigger: '.final', start: 'top 85%', end: 'top 30%', scrub: 0.8 } })
          .fromTo('.final .f.a', { x: '-240%', rotation: 0 }, { x: '-70.7%', rotation: 45, ease: 'power2.inOut' }, 0)
          .fromTo('.final .f.b', { x: '240%', rotation: 0 }, { x: '70.7%', rotation: 45, ease: 'power2.inOut' }, 0)
          .fromTo('.final .p', { scale: 0 }, { scale: 1, ease: 'back.out(3)', duration: 0.25 }, 0.8);
      }

      // Footer wordmark: letters rise and fill with a faint gold.
      if (q('.giant span').length) {
        gsap.from('.giant span', { yPercent: 100, duration: 1, ease: 'expo.out', stagger: 0.05, scrollTrigger: { trigger: '.giant', start: 'top 95%' } });
        gsap.to('.giant span', { color: 'rgba(212,175,55,.08)', duration: 1, stagger: 0.05, scrollTrigger: { trigger: '.giant', start: 'top 85%' } });
      }
    }, root);

    // Late images change section heights; recompute trigger positions.
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    const t = setTimeout(refresh, 1200);
    return () => { window.removeEventListener('load', refresh); clearTimeout(t); ctx.revert(); };
  }, [rootRef, signature]);
}
